import {ActivityIndicator, Linking, StyleSheet} from 'react-native';
import React, {FC, Fragment, memo, useCallback, useState} from 'react';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';

import {Analytics} from '@buncha/config/analytics/analytics';
import {B2BUserScreens} from '@buncha/B2B/navigation/types';
import CancelOrderConfirmationModal from '../CancelOrderConfirmationModal';
import {CustomText} from '@buncha/components/core/text';
import {EventType} from '@buncha/config/analytics/type';
import If from '@buncha/components/conditional/If';
import {Modules} from '@buncha/utils/types';
import {OrderInfoParamsSource} from '../../hooks/type';
import {PButton} from '@buncha/components/core/Button';
import font from '@buncha/appStyles/style/font';
import {meijerSupportNumber} from '@buncha/B2B/utils';
import {useGetLocale} from '@buncha/localization/hooks';

interface Props {
  rescheduleOrder: () => void;
  cancelOrder: () => void;
  rescheduleDisabled: boolean;
  rescheduleOrderLoading: boolean;
  cancelOrderLoading: boolean;
  cancelOrderDisabled: boolean;
  runId: number;
  legacyId: number;
}

const RescheduleFooter: FC<Props> = props => {
  const muli = useFontTheme('muli');
  const theme = useColorThemeType();
  const {button} =
    useGetLocale(Modules.B2BUserOrder, B2BUserScreens.Reschedule) ?? {};
  const {
    rescheduleOrder,
    cancelOrder,
    rescheduleDisabled,
    rescheduleOrderLoading,
    cancelOrderLoading,
    cancelOrderDisabled,
    runId,
    legacyId,
  } = props;
  const [cancelModal, setCancelModal] = useState(false);

  const callHandler = useCallback(() => {
    Analytics.eventWithProps('Meijer Support', {
      type: EventType.cta,
      runId,
      orderId: legacyId,
      source: OrderInfoParamsSource.Reschedule,
    });
    Linking.openURL(`tel:+${meijerSupportNumber}`);
  }, [legacyId, runId]);

  const toggleCancelOrderModal = useCallback(() => {
    setCancelModal(open => !open);
  }, []);

  const handleSubmitCancelOrder = useCallback(() => {
    cancelOrder();
    toggleCancelOrderModal();
  }, [cancelOrder, toggleCancelOrderModal]);

  const buttonLoader = <ActivityIndicator style={styles.spinner} />;

  return (
    <Fragment>
      <PButton
        titleColor={theme?.text.blue[300]}
        bgColor={theme?.background.yellow[100]}
        style={styles.reschedule}
        containerStyle={styles.rescheduleContainer}
        titleStyle={{
          ...font.FontSize.fontH5,
          fontFamily: muli[700].normal,
        }}
        title={button?.reschedule}
        onPress={rescheduleOrder}
        disabled={rescheduleDisabled}
        changeStyleOnLoading
        rightComponent={
          <If condition={rescheduleOrderLoading}>{buttonLoader}</If>
        }
      />
      <PButton
        type="outline"
        titleColor={theme?.text.blue[300]}
        bgColor="transparent"
        borderColor={theme?.background.gray[100]}
        style={styles.cancel}
        borderWidth={1}
        containerStyle={styles.cancelContainer}
        titleStyle={{
          ...font.FontSize.fontH5,
          fontFamily: muli[700].normal,
        }}
        title={button?.cancel}
        onPress={toggleCancelOrderModal}
        disabled={cancelOrderDisabled}
        changeStyleOnLoading
        rightComponent={<If condition={cancelOrderLoading}>{buttonLoader}</If>}
      />
      <CustomText
        onPress={callHandler}
        style={[
          styles.support,
          {
            fontFamily: muli[400].normal,
            color: theme?.text.blue[500],
          },
        ]}>
        {button?.support}
      </CustomText>
      <CancelOrderConfirmationModal
        open={cancelModal}
        onClose={toggleCancelOrderModal}
        onSubmit={handleSubmitCancelOrder}
      />
    </Fragment>
  );
};

export default memo(RescheduleFooter);

const styles = StyleSheet.create({
  reschedule: {marginTop: 25},
  rescheduleContainer: {borderRadius: 5},
  cancel: {marginTop: 12},
  cancelContainer: {borderRadius: 5},
  spinner: {marginLeft: 10},
  support: {
    marginTop: 12,
    textDecorationLine: 'underline',
  },
});
